import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Activity, HeartPulse, Sparkles, Flame, Shield, ArrowUpRight, 
  Crown, Lock, ChevronRight, CheckCircle2, AlertTriangle, 
  Droplets, Zap, Moon, Plus, RefreshCw, Info, FileText, Check, Clock, TrendingUp
} from 'lucide-react';
import { 
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, 
  Tooltip, CartesianGrid, ReferenceLine, LineChart, Line, BarChart, Bar 
} from 'recharts';
import { cn } from '../lib/utils';
import { 
  HistoryEntry, 
  FoodItem, 
  BiomarkerLog, 
  SubscriptionTier, 
  Language, 
  GlycemicSpikeEvent, 
  CompensationProtocol 
} from '../types';
import { 
  calculateHealthROI, 
  generateHealthRoiTrajectory, 
  detectGlycemicSpikes, 
  generateCompensationProgram,
  DEFAULT_BIOMARKER_LOGS
} from '../services/longevityEngine';
import { generateEcosystemAiSynthesis } from '../services/nutritionService';
import { BiomarkerLogModal } from './BiomarkerLogModal';
import { triggerHaptic } from '../lib/haptic';
import { sounds } from '../lib/sound';

interface EcosystemDashboardProps {
  history: HistoryEntry[];
  todayFoods: FoodItem[];
  biomarkers?: BiomarkerLog[];
  subscriptionTier: SubscriptionTier;
  onOpenUpgradeModal: () => void;
  lang: Language;
  theme: 'light' | 'dark';
  skin?: string;
  stepsToday?: number;
  sleepHours?: number;
  onSaveBiomarker?: (log: Partial<BiomarkerLog>) => void;
}

export const EcosystemDashboard: React.FC<EcosystemDashboardProps> = ({
  history,
  todayFoods,
  biomarkers = DEFAULT_BIOMARKER_LOGS,
  subscriptionTier,
  onOpenUpgradeModal,
  lang,
  theme,
  skin = 'default',
  stepsToday = 8500,
  sleepHours = 7.5,
  onSaveBiomarker
}) => {
  const isUa = lang === 'ua';
  const isStalker = skin === 'stalker';
  const isPremium = subscriptionTier === 'premium' || subscriptionTier === 'premium_pro';

  // Sub-tabs: longevity, biomarkers, sentinel
  const [subTab, setSubTab] = useState<'longevity' | 'biomarkers' | 'sentinel'>('longevity');
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Record<string, boolean>>({});
  
  // AI Dynamic Synthesis State
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const [aiProjections, setAiProjections] = useState<{
    year1?: string;
    year3?: string;
    year5?: string;
    compensationAdvice?: string;
    modelUsed?: string;
  } | null>(null);

  // 1. Calculate Longevity Metrics & ROI
  const metrics = useMemo(() => {
    return calculateHealthROI(history, biomarkers, todayFoods, stepsToday, sleepHours);
  }, [history, biomarkers, todayFoods, stepsToday, sleepHours]);

  const roiTrajectory = useMemo(() => {
    return generateHealthRoiTrajectory(metrics, lang);
  }, [metrics, lang]);

  // 2. Detect Latest Glycemic Spike & Compensation
  const latestSpike = useMemo(() => {
    // Check recent meals in today or history
    if (history.length > 0) {
      for (const entry of history.slice(0, 5)) {
        const spike = detectGlycemicSpikes(entry);
        if (spike) return spike;
      }
    }
    // Fallback demo spike if none present so user can test and experience compensation
    return {
      mealId: 'demo-spike',
      mealName: isUa ? 'Глазурований круасан з карамельним лате' : 'Glazed Croissant & Caramel Latte',
      timestamp: Date.now() - 3600 * 1000 * 1.5,
      estimatedSpikeMgDl: 154,
      glycemicLoad: 28,
      severity: 'moderate' as const,
      triggerFoods: isUa ? ['Круасан з карамеллю', 'Сироп лате'] : ['Caramel Croissant', 'Latte Syrup']
    };
  }, [history, isUa]);

  const compensationProtocol = useMemo(() => {
    return generateCompensationProgram(latestSpike, lang);
  }, [latestSpike, lang]);

  // Toggle compensation checklist item
  const toggleStep = (stepId: string) => {
    triggerHaptic('SUCCESS');
    sounds.playPop();
    setCompletedSteps(prev => ({
      ...prev,
      [stepId]: !prev[stepId]
    }));
  };

  // Recent Biomarker
  const currentBio = biomarkers[0] || DEFAULT_BIOMARKER_LOGS[0];

  return (
    <div className="space-y-6 px-2 pb-[calc(6rem+env(safe-area-inset-bottom))]">
      {/* 1. Header Banner & Plan Status */}
      <div className={cn(
        "rounded-[32px] p-6 sm:p-8 relative overflow-hidden border transition-all",
        isStalker 
          ? "bg-black border-[#4ade80]/40 text-[#4ade80] font-mono"
          : theme === 'light'
            ? "bg-gradient-to-br from-emerald-500/10 via-teal-500/5 to-purple-500/10 border-emerald-500/20 text-zinc-900 shadow-xl"
            : "bg-gradient-to-br from-emerald-950/40 via-zinc-900 to-purple-950/30 border-emerald-500/20 text-zinc-100 card-shadow"
      )}>
        {/* Glow Spheres & Bio Tech Grid */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-0" aria-hidden="true">
          <div className="absolute inset-0 bg-tech-grid opacity-35 dark:opacity-25" style={{ backgroundSize: '24px 24px' }} />
          <motion.div
            animate={{
              x: [0, 35, -25, 0],
              y: [0, -20, 15, 0],
              scale: [1, 1.3, 0.9, 1],
              opacity: [0.35, 0.65, 0.4, 0.35]
            }}
            transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
            className={cn(
              "absolute -top-16 -left-12 w-64 h-64 rounded-full blur-3xl",
              isStalker ? "bg-[#4ade80]/20" : "bg-emerald-400/25 dark:bg-emerald-500/20"
            )}
          />
          <motion.div
            animate={{
              x: [0, -30, 20, 0],
              y: [0, 25, -20, 0],
              scale: [1, 1.2, 0.95, 1],
              opacity: [0.3, 0.6, 0.35, 0.3]
            }}
            transition={{ duration: 13, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
            className={cn(
              "absolute -bottom-16 -right-12 w-64 h-64 rounded-full blur-3xl",
              isStalker ? "bg-amber-400/15" : "bg-purple-500/20 dark:bg-purple-600/25"
            )}
          />
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 relative z-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className={cn(
                "px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider flex items-center gap-1",
                isPremium 
                  ? "bg-purple-500/20 text-purple-400 border border-purple-500/30"
                  : "bg-amber-500/20 text-amber-500 border border-amber-500/30"
              )}>
                {isPremium ? <Crown className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                <span>
                  {isStalker 
                    ? (isPremium ? "ZONE BIO-OVERRIDE UNLOCKED" : "TIER: STALKER ROOKIE")
                    : (isPremium ? (isUa ? "Преміум Біо-Хакінг" : "Premium Bio-Hacking") : (isUa ? "Демо-Режим" : "Preview Mode"))}
                </span>
              </span>

              <span className="text-xs font-bold text-emerald-500 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                <span>{isUa ? "Синхронізація активна" : "Biometrics Live"}</span>
              </span>
            </div>

            <h2 className="text-xl sm:text-2xl font-black uppercase tracking-tight">
              {isStalker 
                ? (isUa ? "ЗОННИЙ БІО-МОНІТОР & АНОМАЛЬНА ДОЗИМЕТРІЯ" : "ZONE BIO-MONITOR & METABOLIC DOSIMETRY")
                : (isUa ? "Екосистема Довголіття та Біомаркерів" : "Longevity & Biomarker Ecosystem")}
            </h2>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 max-w-lg">
              {isStalker 
                ? (isUa ? "Контроль клітинної глікації (AGEs), гліко-перевантажень та автономного відновлення." : "Tracking cellular glycation (dAGEs), glycemic overloads & autonomic restoration.")
                : (isUa ? "Моніторинг AGEs-маркерів, компенсація стрибків цукру та динамічна проекція біологічного віку." : "Real-time AGEs tracking, postprandial spike compensation & biological age trajectory.")}
            </p>
          </div>

          <div className="flex items-center gap-2.5 shrink-0 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => {
                triggerHaptic('SUCCESS');
                setIsLogModalOpen(true);
              }}
              className={cn(
                "px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-md flex-1 sm:flex-initial",
                isStalker 
                  ? "bg-[#4ade80] text-black hover:bg-[#4ade80]/90"
                  : "bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-500/20"
              )}
            >
              <Plus className="w-3.5 h-3.5" />
              <span>{isUa ? "Записати біомаркер" : "Log Biomarker"}</span>
            </button>

            {!isPremium && (
              <button
                type="button"
                onClick={() => {
                  triggerHaptic('SUCCESS');
                  onOpenUpgradeModal();
                }}
                className="px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-wider bg-purple-500 hover:bg-purple-600 text-white transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-lg shadow-purple-500/20 shrink-0"
              >
                <Crown className="w-3.5 h-3.5" />
                <span>{isUa ? "Розблокувати" : "Upgrade"}</span>
              </button>
            )}
          </div>
        </div>

        {/* Dynamic Vitals Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-6 border-t border-zinc-200/60 dark:border-zinc-800/80">
          <div className="p-3.5 rounded-2xl bg-zinc-100/60 dark:bg-zinc-900/60 border border-zinc-200/80 dark:border-zinc-800/80">
            <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
              {isStalker ? (isUa ? "БІОЛОГІЧНИЙ ДЕЛЬТА-ВІК" : "BIO-AGE DELTA") : (isUa ? "Біологічний вік" : "Biological Age Delta")}
            </div>
            <div className="text-lg sm:text-xl font-black text-emerald-500 mt-1 flex items-baseline gap-1">
              <span>{metrics.biological_age_delta > 0 ? `+${metrics.biological_age_delta}` : metrics.biological_age_delta}</span>
              <span className="text-xs font-bold text-zinc-400">{isUa ? "років" : "yrs"}</span>
            </div>
            <span className="text-[10px] text-zinc-500 dark:text-zinc-400">
              {metrics.biological_age_delta < 0 ? (isUa ? "Молодший за норму" : "Younger than norm") : (isUa ? "Потребує оптимізації" : "Requires tuning")}
            </span>
          </div>

          <div className="p-3.5 rounded-2xl bg-zinc-100/60 dark:bg-zinc-900/60 border border-zinc-200/80 dark:border-zinc-800/80">
            <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
              {isStalker ? (isUa ? "ЗОННИЙ ДИВІДЕНД" : "SURVIVAL DIVIDEND") : (isUa ? "Дивіденд довголіття" : "Longevity Dividend")}
            </div>
            <div className="text-lg sm:text-xl font-black text-purple-400 mt-1 flex items-baseline gap-1">
              <span>{metrics.longevity_dividend_minutes > 0 ? `+${(metrics.longevity_dividend_minutes / 60).toFixed(1)}` : (metrics.longevity_dividend_minutes / 60).toFixed(1)}</span>
              <span className="text-xs font-bold text-zinc-400">{isUa ? "год/день" : "hrs/day"}</span>
            </div>
            <span className="text-[10px] text-zinc-500 dark:text-zinc-400">
              {isUa ? `Разом: +${metrics.cumulative_dividend_hours} год за 30 днів` : `Cumul: +${metrics.cumulative_dividend_hours}h / 30d`}
            </span>
          </div>

          <div className="p-3.5 rounded-2xl bg-zinc-100/60 dark:bg-zinc-900/60 border border-zinc-200/80 dark:border-zinc-800/80">
            <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
              {isStalker ? (isUa ? "ГЛІКАЦІЯ КЛІТИН (AGEs)" : "CELL CORROSION (AGEs)") : (isUa ? "Маркер AGEs (Сьогодні)" : "Daily Dietary AGEs")}
            </div>
            <div className={cn(
              "text-lg sm:text-xl font-black mt-1 flex items-baseline gap-1",
              metrics.ages_threshold_status === 'optimal' ? "text-emerald-500" :
              metrics.ages_threshold_status === 'moderate' ? "text-amber-500" : "text-red-500"
            )}>
              <span>{metrics.daily_ages_ku.toLocaleString()}</span>
              <span className="text-xs font-bold text-zinc-400">kU</span>
            </div>
            <span className="text-[10px] text-zinc-500 dark:text-zinc-400">
              {metrics.ages_threshold_status === 'optimal' ? (isUa ? "Оптимальний рівень" : "Optimal range (<15k)") :
               metrics.ages_threshold_status === 'moderate' ? (isUa ? "Помірне окислення" : "Moderate glycation") : (isUa ? "Високий ризик!" : "High cross-linking")}
            </span>
          </div>

          <div className="p-3.5 rounded-2xl bg-zinc-100/60 dark:bg-zinc-900/60 border border-zinc-200/80 dark:border-zinc-800/80">
            <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
              {isStalker ? (isUa ? "ВАРІАБЕЛЬНІСТЬ (HRV)" : "AUTONOMIC HRV") : (isUa ? "Варіабельність HRV" : "Autonomic HRV")}
            </div>
            <div className="text-lg sm:text-xl font-black text-indigo-400 mt-1 flex items-baseline gap-1">
              <span>{currentBio.hrv_rmssd || 68}</span>
              <span className="text-xs font-bold text-zinc-400">ms</span>
            </div>
            <span className="text-[10px] text-zinc-500 dark:text-zinc-400">
              {isUa ? "Високий відновлювальний тонус" : "Restorative parasympathetic"}
            </span>
          </div>
        </div>
      </div>

      {/* 2. Sub-Tab Selectors */}
      <div className="flex items-center gap-2 p-1.5 rounded-2xl border border-zinc-200/80 dark:border-zinc-800/80 bg-zinc-100/70 dark:bg-zinc-900/70 backdrop-blur-md">
        {[
          { id: 'longevity', labelUa: '🧬 Двигун довголіття & ROI', labelEn: '🧬 Longevity Engine & ROI', icon: TrendingUp },
          { id: 'biomarkers', labelUa: '💓 Біомаркери & Проекції', labelEn: '💓 Vitals & Projections', icon: HeartPulse },
          { id: 'sentinel', labelUa: '⚡ Запалення & Спайки', labelEn: '⚡ Inflammation & Spikes', icon: Zap },
        ].map(item => {
          const active = subTab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                triggerHaptic('SUCCESS');
                setSubTab(item.id as any);
              }}
              className={cn(
                "flex-1 py-2.5 px-3 rounded-xl text-[11px] sm:text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 cursor-pointer truncate",
                active 
                  ? (isStalker 
                      ? "bg-[#4ade80] text-black shadow-lg shadow-[#4ade80]/20" 
                      : "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20")
                  : "text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200"
              )}
            >
              <span>{isUa ? item.labelUa : item.labelEn}</span>
            </button>
          );
        })}
      </div>

      {/* 3. Non-Premium Preview Banner Trigger (if Free/Pro) */}
      {!isPremium && (
        <div className="p-4 rounded-2xl border border-purple-500/30 bg-purple-500/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-purple-500/20 text-purple-400 flex items-center justify-center shrink-0">
              <Crown className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs font-black uppercase tracking-wider text-purple-300">
                {isUa ? "Перегляд преміум біо-хакінгу (Демо-дані)" : "Bio-Hacking Preview Mode (Sample Vitals)"}
              </div>
              <p className="text-[11px] text-zinc-400">
                {isUa ? "Підключіть реальні біомаркери, кастомні аналізи та персональну компенсацію гліко-спайків." : "Unlock live continuous biomarker correlation and personalized postprandial protocols."}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              triggerHaptic('SUCCESS');
              onOpenUpgradeModal();
            }}
            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer shrink-0 shadow-md"
          >
            {isUa ? "Оновити до Premium" : "Upgrade to Premium"}
          </button>
        </div>
      )}

      {/* 4. Tab 1: Longevity & ROI */}
      {subTab === 'longevity' && (
        <div className="space-y-6">
          {/* Health ROI Trajectory Chart */}
          <div className={cn(
            "p-6 rounded-3xl border transition-all",
            isStalker 
              ? "bg-black border-[#4ade80]/40 text-[#4ade80]" 
              : theme === 'light' ? "bg-white border-zinc-200" : "bg-zinc-900 border-zinc-800"
          )}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-black uppercase tracking-wider flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-500" />
                  <span>{isStalker ? (isUa ? "ГРАФІК ТРАЄКТОРІЇ ЗОННОГО ВИЖИВАННЯ (HEALTH ROI)" : "ZONE SURVIVABILITY TRAJECTORY (HEALTH ROI)") : (isUa ? "Графік віддачі здоров'я (Health ROI Graph)" : "Longevity Health ROI Graph")}</span>
                </h3>
                <p className="text-xs text-zinc-500">
                  {isUa ? "Порівняння вашої фактичної траєкторії довголіття з базовим хронологічним згасанням." : "Compares your actual longevity trajectory against baseline chronological decline."}
                </p>
              </div>
              <div className="flex items-center gap-3 text-[11px] font-bold">
                <span className="flex items-center gap-1 text-emerald-500">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <span>{isUa ? "Ваш ROI" : "Your Trajectory"}</span>
                </span>
                <span className="flex items-center gap-1 text-zinc-400">
                  <div className="w-2.5 h-2.5 rounded-full bg-zinc-400" />
                  <span>{isUa ? "Базове старіння" : "Baseline Decay"}</span>
                </span>
              </div>
            </div>

            <div className="h-64 sm:h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={roiTrajectory} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                  <defs>
                    <linearGradient id="roiGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={isStalker ? "#4ade80" : "#10b981"} stopOpacity={0.4}/>
                      <stop offset="95%" stopColor={isStalker ? "#4ade80" : "#10b981"} stopOpacity={0.0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }} dy={10} />
                  <YAxis domain={[20, 100]} axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff', 
                      borderRadius: '16px', 
                      border: '1px solid #27272a',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }} 
                  />
                  <ReferenceLine y={50} stroke="#71717a" strokeDasharray="3 3" label={{ value: 'Break-even', fill: '#71717a', fontSize: 10 }} />
                  <Area 
                    type="monotone" 
                    dataKey="healthRoi" 
                    stroke={isStalker ? "#4ade80" : "#10b981"} 
                    strokeWidth={3} 
                    fillOpacity={1} 
                    fill="url(#roiGradient)" 
                    name={isUa ? "Health ROI" : "Health ROI Score"}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="baseline" 
                    stroke="#71717a" 
                    strokeWidth={1.5} 
                    strokeDasharray="4 4" 
                    dot={false}
                    name={isUa ? "Базовий спад" : "Baseline"}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* AGEs Cellular Markers Dial & Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={cn(
              "p-6 rounded-3xl border transition-all",
              isStalker 
                ? "bg-black border-[#4ade80]/40 text-[#4ade80]" 
                : theme === 'light' ? "bg-white border-zinc-200" : "bg-zinc-900 border-zinc-800"
            )}>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-black uppercase tracking-wider flex items-center gap-1.5">
                  <Flame className="w-4 h-4 text-amber-500" />
                  <span>{isStalker ? (isUa ? "ДОЗИМЕТРІЯ КЛІТИННОЇ ГЛІКАЦІЇ" : "CELLULAR GLYCATION DOSIMETRY") : (isUa ? "Рівень глікації тканин (dAGEs)" : "Dietary AGEs Exposure")}</span>
                </h4>
                <span className={cn(
                  "px-2 py-0.5 rounded-md text-[10px] font-black uppercase",
                  metrics.ages_threshold_status === 'optimal' ? "bg-emerald-500/20 text-emerald-500" :
                  metrics.ages_threshold_status === 'moderate' ? "bg-amber-500/20 text-amber-500" : "bg-red-500/20 text-red-500"
                )}>
                  {metrics.ages_threshold_status}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mb-4">
                {isUa 
                  ? "Кінцеві продукти глікації (AGEs) утворюються при високотемпературній смаженні та викликають затвердіння судин і старіння шкіри."
                  : "Advanced Glycation End-products cross-link collagen, stiffen arteries, and trigger the RAGE inflammatory cascade."}
              </p>

              {/* Progress bar with threshold zones */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs font-bold">
                  <span>0 kU</span>
                  <span className="text-emerald-500">15,000 kU (Ціль)</span>
                  <span className="text-red-500">22,000+ kU</span>
                </div>
                <div className="w-full h-3 rounded-full bg-zinc-200 dark:bg-zinc-800 overflow-hidden relative">
                  <div 
                    className={cn(
                      "h-full rounded-full transition-all duration-500",
                      metrics.ages_threshold_status === 'optimal' ? "bg-emerald-500" :
                      metrics.ages_threshold_status === 'moderate' ? "bg-amber-500" : "bg-red-500"
                    )}
                    style={{ width: `${Math.min(100, (metrics.daily_ages_ku / 25000) * 100)}%` }}
                  />
                </div>
              </div>

              <div className="mt-4 p-3 rounded-2xl bg-zinc-100/80 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700/60 text-xs flex items-center gap-2.5">
                <Shield className="w-4 h-4 text-emerald-500 shrink-0" />
                <span className="text-zinc-600 dark:text-zinc-300">
                  {isUa 
                    ? "Порада: Маринування м’яса в лимонному соку або оцті знижує утворення AGEs на 50%!" 
                    : "Tip: Acidic marinades (lemon juice, vinegar) reduce high-heat AGE formation by up to 50%!"}
                </span>
              </div>
            </div>

            {/* Top AGEs and Inhibitor Foods */}
            <div className={cn(
              "p-6 rounded-3xl border transition-all",
              isStalker 
                ? "bg-black border-[#4ade80]/40 text-[#4ade80]" 
                : theme === 'light' ? "bg-white border-zinc-200" : "bg-zinc-900 border-zinc-800"
            )}>
              <h4 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-purple-400" />
                <span>{isUa ? "Фактори впливу на глікацію" : "Glycation Drivers & Inhibitors"}</span>
              </h4>
              <div className="space-y-2.5 text-xs">
                <div className="flex items-center justify-between p-2.5 rounded-xl bg-red-500/10 border border-red-500/20">
                  <span className="font-bold text-red-400">{isUa ? "Смажене / гриль м'ясо" : "Grilled/fried fatty meats"}</span>
                  <span className="font-mono text-zinc-400">7,000–12,000 kU/100g</span>
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <span className="font-bold text-amber-400">{isUa ? "Тверді сири & випічка" : "Aged cheeses & baked goods"}</span>
                  <span className="font-mono text-zinc-400">2,500–4,500 kU/100g</span>
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                  <span className="font-bold text-emerald-400">{isUa ? "Ягоди, шпинат, зелений чай" : "Berries, greens & green tea"}</span>
                  <span className="font-bold text-emerald-400">{isUa ? "Блокатори AGEs" : "RAGE Inhibitors"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 5. Tab 2: Vitals & Dynamic Projections */}
      {subTab === 'biomarkers' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* HRV Card */}
            <div className={cn(
              "p-5 rounded-3xl border transition-all",
              isStalker ? "bg-black border-[#4ade80]/40 text-[#4ade80]" : theme === 'light' ? "bg-white border-zinc-200" : "bg-zinc-900 border-zinc-800"
            )}>
              <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
                <span>HRV (rMSSD)</span>
                <HeartPulse className="w-4 h-4 text-pink-500" />
              </div>
              <div className="text-2xl font-black text-emerald-500 flex items-baseline gap-1">
                <span>{currentBio.hrv_rmssd || 68}</span>
                <span className="text-xs font-normal text-zinc-400">ms</span>
              </div>
              <p className="text-[11px] text-zinc-500 mt-2">
                {isUa ? "Високий резерв відновлення ЦНС." : "High autonomic parasympathetic recovery."}
              </p>
            </div>

            {/* Glucose Dynamics Card */}
            <div className={cn(
              "p-5 rounded-3xl border transition-all",
              isStalker ? "bg-black border-[#4ade80]/40 text-[#4ade80]" : theme === 'light' ? "bg-white border-zinc-200" : "bg-zinc-900 border-zinc-800"
            )}>
              <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
                <span>{isUa ? "Глюкоза (Натщесерце)" : "Fasting Glucose"}</span>
                <Droplets className="w-4 h-4 text-blue-500" />
              </div>
              <div className="text-2xl font-black text-blue-400 flex items-baseline gap-1">
                <span>{currentBio.fasting_glucose_mgdl || 86}</span>
                <span className="text-xs font-normal text-zinc-400">mg/dL</span>
              </div>
              <p className="text-[11px] text-zinc-500 mt-2">
                {isUa ? "Ідеальний метаболічний гомеостаз." : "Optimal metabolic insulin sensitivity."}
              </p>
            </div>

            {/* Sleep Architecture Card */}
            <div className={cn(
              "p-5 rounded-3xl border transition-all",
              isStalker ? "bg-black border-[#4ade80]/40 text-[#4ade80]" : theme === 'light' ? "bg-white border-zinc-200" : "bg-zinc-900 border-zinc-800"
            )}>
              <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
                <span>{isUa ? "Глибокий сон" : "Deep Sleep"}</span>
                <Moon className="w-4 h-4 text-indigo-500" />
              </div>
              <div className="text-2xl font-black text-indigo-400 flex items-baseline gap-1">
                <span>{currentBio.deep_sleep_minutes || 105}</span>
                <span className="text-xs font-normal text-zinc-400">{isUa ? "хв" : "min"}</span>
              </div>
              <p className="text-[11px] text-zinc-500 mt-2">
                {isUa ? "Повне глімфатичне очищення мозку." : "Sufficient glymphatic brain clearance."}
              </p>
            </div>

            {/* Blood Labs Card */}
            <div className={cn(
              "p-5 rounded-3xl border transition-all",
              isStalker ? "bg-black border-[#4ade80]/40 text-[#4ade80]" : theme === 'light' ? "bg-white border-zinc-200" : "bg-zinc-900 border-zinc-800"
            )}>
              <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
                <span>hs-CRP & HbA1c</span>
                <Shield className="w-4 h-4 text-purple-500" />
              </div>
              <div className="text-2xl font-black text-purple-400 flex items-baseline gap-1">
                <span>{currentBio.hs_crp_mgl || 0.6}</span>
                <span className="text-xs font-normal text-zinc-400">mg/L</span>
              </div>
              <p className="text-[11px] text-zinc-500 mt-2">
                {isUa ? "HbA1c: 5.1% (Мінімальне системне запалення)." : "HbA1c: 5.1% (Minimal systemic inflammation)."}
              </p>
            </div>
          </div>

          {/* Dynamic AI Health Projections Card */}
          <div className={cn(
            "p-6 rounded-3xl border transition-all relative overflow-hidden",
            isStalker ? "bg-black border-[#4ade80]/40 text-[#4ade80]" : theme === 'light' ? "bg-white border-zinc-200" : "bg-zinc-900 border-zinc-800"
          )}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
              <div>
                <h3 className="text-base font-black uppercase tracking-wider flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  <span>{isStalker ? (isUa ? "ПРОГНОСТИЧНА МАТРИЦЯ ЗОНИ (5 РОКІВ)" : "ZONE 5-YEAR SURVIVABILITY PROJECTION") : (isUa ? "Динамічні прогнози здоров'я (ШІ Синтез)" : "AI Dynamic Health Projections")}</span>
                </h3>
                <p className="text-xs text-zinc-500 max-w-xl">
                  {isUa 
                    ? `ШІ ${isPremium ? 'Gemini 1.5 Pro' : 'Gemini 3.7 Flash'} синтезує ваш профіль харчування, біомаркери HRV та показники сну для прогнозу метаболічної стабільності.`
                    : `${isPremium ? 'Gemini 1.5 Pro' : 'Gemini 3.7 Flash'} synthesizes your meal glycemic loads, nocturnal HRV, and sleep architecture.`}
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <span className={cn(
                  "px-2.5 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider flex items-center gap-1",
                  isPremium ? "bg-purple-500/20 text-purple-400 border border-purple-500/30" : "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20"
                )}>
                  {isPremium ? <Crown className="w-3 h-3 text-purple-400" /> : <Sparkles className="w-3 h-3 text-emerald-500" />}
                  <span>{isPremium ? "Gemini 1.5 Pro (Premium)" : "Gemini 3.7 Flash"}</span>
                </span>

                <button
                  type="button"
                  disabled={isGeneratingAi}
                  onClick={async () => {
                    if (!isPremium) {
                      triggerHaptic('WARNING');
                      onOpenUpgradeModal();
                      return;
                    }
                    triggerHaptic('SUCCESS');
                    sounds.playPop();
                    setIsGeneratingAi(true);
                    try {
                      const res = await generateEcosystemAiSynthesis({
                        metrics,
                        biomarkers,
                        todayFoods,
                        latestSpike,
                        subscriptionTier,
                        lang
                      });
                      setAiProjections(res);
                    } catch (e) {
                      console.error(e);
                    } finally {
                      setIsGeneratingAi(false);
                    }
                  }}
                  className={cn(
                    "px-3.5 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-1.5 transition-all cursor-pointer shadow-md disabled:opacity-60",
                    isStalker 
                      ? "bg-[#4ade80] text-black hover:bg-[#4ade80]/90"
                      : isPremium 
                        ? "bg-purple-600 hover:bg-purple-500 text-white shadow-purple-500/20" 
                        : "bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-500/20"
                  )}
                >
                  <RefreshCw className={cn("w-3.5 h-3.5", isGeneratingAi && "animate-spin")} />
                  <span>{isGeneratingAi ? (isUa ? "Синтез..." : "Synthesizing...") : (isUa ? "ШІ Оновлення" : "Run AI Synthesis")}</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs mt-4">
              <div className="p-4 rounded-2xl bg-zinc-100/70 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700/60">
                <div className="font-black text-emerald-500 text-sm mb-1">{isUa ? "1 Рік: Метаболічна гнучкість" : "1 Year: Metabolic Agility"}</div>
                <p className="text-zinc-600 dark:text-zinc-400">
                  {aiProjections?.year1 || (isUa ? "Зниження амплітуди глюкози на 28%, оптимізація чутливості до інсуліну." : "28% dampening in glycemic volatility and enhanced mitochondrial fuel switching.")}
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-zinc-100/70 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700/60">
                <div className="font-black text-indigo-400 text-sm mb-1">{isUa ? "3 Роки: Кардіо-судинна еластичність" : "3 Years: Vascular Elasticity"}</div>
                <p className="text-zinc-600 dark:text-zinc-400">
                  {aiProjections?.year3 || (isUa ? "Захист ендотелію від глікаційного склеювання (dAGEs), стабільний артеріальний тиск." : "Arterial compliance preserved via reduced collagen cross-linking; stable pulse wave.")}
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-zinc-100/70 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700/60">
                <div className="font-black text-purple-400 text-sm mb-1">{isUa ? "5 Років: Довголіття (Healthspan)" : "5 Years: Extended Healthspan"}</div>
                <p className="text-zinc-600 dark:text-zinc-400">
                  {aiProjections?.year5 || (isUa ? "Проектований біологічний вік на 3.5 роки молодший за паспортний." : "Projected biological telomere and epigenetic age divergence: -3.5 years.")}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 6. Tab 3: Inflammation & Spikes Sentinel + Compensation */}
      {subTab === 'sentinel' && (
        <div className="space-y-6">
          {/* Inflammation Score & Glycemic Spike Alert */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={cn(
              "p-6 rounded-3xl border transition-all",
              isStalker ? "bg-black border-[#4ade80]/40 text-[#4ade80]" : theme === 'light' ? "bg-white border-zinc-200" : "bg-zinc-900 border-zinc-800"
            )}>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-black uppercase tracking-wider flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  <span>{isUa ? "Індекс запалення раціону (DII)" : "Dietary Inflammatory Index (DII)"}</span>
                </h4>
                <span className={cn(
                  "px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider",
                  metrics.inflammation_status === 'anti_inflammatory' ? "bg-emerald-500/20 text-emerald-500" : "bg-red-500/20 text-red-500"
                )}>
                  {metrics.dietary_inflammatory_index > 0 ? `+${metrics.dietary_inflammatory_index}` : metrics.dietary_inflammatory_index}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mb-4">
                {isUa 
                  ? "Оцінює співвідношення Омега-3, клітковини та поліфенолів до транс-жирів, рафінованих олій та цукру."
                  : "Assesses pro-inflammatory lipids & refined sugars against anti-inflammatory polyphenols & omega-3."}
              </p>

              <div className="flex items-center justify-between text-xs font-bold pt-2 border-t border-zinc-200 dark:border-zinc-800">
                <span className="text-emerald-500">{isUa ? "Антизапальний статус" : "Anti-Inflammatory"}</span>
                <span className="text-zinc-400">{isUa ? "Нейтрально" : "Neutral"}</span>
                <span className="text-red-500">{isUa ? "Хронічний ризик" : "Pro-Inflammatory"}</span>
              </div>
            </div>

            {/* Spike Detector Summary */}
            <div className={cn(
              "p-6 rounded-3xl border transition-all",
              isStalker ? "bg-black border-[#4ade80]/40 text-[#4ade80]" : theme === 'light' ? "bg-white border-zinc-200" : "bg-zinc-900 border-zinc-800"
            )}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-black uppercase tracking-wider flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <span>{isStalker ? (isUa ? "СЕНСОР ГЛІКО-ПЕРЕВАНТАЖЕНЬ" : "GLYCO-OVERLOAD SENSOR") : (isUa ? "Сенсор стрибків цукру (Spikes)" : "Rapid Glycemic Spike Sentinel")}</span>
                </h4>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-amber-500/20 text-amber-500">
                  {latestSpike.severity}
                </span>
              </div>
              <div className="text-xs text-zinc-500 mb-3">
                {isUa ? "Останнє зафіксоване навантаження:" : "Recent detected spike trigger:"} <strong className="text-[var(--text)]">{latestSpike.mealName}</strong>
              </div>
              <div className="flex items-center gap-4 text-xs font-bold">
                <div>
                  <span className="text-zinc-400 block text-[10px] uppercase">{isUa ? "Піковий стрибок" : "Est. Spike"}</span>
                  <span className="text-amber-500 text-lg font-black">+{latestSpike.estimatedSpikeMgDl} mg/dL</span>
                </div>
                <div className="h-8 w-px bg-zinc-200 dark:bg-zinc-800" />
                <div>
                  <span className="text-zinc-400 block text-[10px] uppercase">{isUa ? "Глікемічне навантаження" : "Glycemic Load"}</span>
                  <span className="text-purple-400 text-lg font-black">{latestSpike.glycemicLoad} GL</span>
                </div>
              </div>
            </div>
          </div>

          {/* ACTIVE ACTIONABLE COMPENSATION PROGRAM */}
          <div className={cn(
            "p-6 sm:p-8 rounded-3xl border transition-all",
            isStalker 
              ? "bg-black border-[#4ade80] text-[#4ade80]" 
              : theme === 'light'
                ? "bg-gradient-to-br from-emerald-500/5 to-teal-500/5 border-emerald-500/30"
                : "bg-gradient-to-br from-emerald-950/20 via-zinc-900 to-zinc-900 border-emerald-500/30"
          )}>
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-500 border border-emerald-500/30 inline-block mb-2">
                  {isStalker ? (isUa ? "ПРОТОКОЛ ЗОНИ: ОЧИЩЕННЯ ТА СТАБІЛІЗАЦІЯ" : "ZONE PROTOCOL: STABILIZATION PURGE") : (isUa ? "Клінічна програма компенсації" : "Active Compensation Protocol")}
                </span>
                <h3 className="text-lg sm:text-xl font-black uppercase tracking-tight">
                  {isUa ? `Компенсація після: ${latestSpike.mealName}` : `Postprandial Protocol for: ${latestSpike.mealName}`}
                </h3>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                  {isUa 
                    ? "Покроковий протокол для нейтралізації запального стресу, утилізації глюкози через GLUT-4 та захисту судин від AGEs."
                    : "Clinical-grade steps to clear blood glucose without insulin resistance and shield vascular collagen."}
                </p>
              </div>

              <div className="text-right shrink-0">
                <div className="text-[10px] font-bold uppercase text-zinc-400">{isUa ? "Виконано" : "Progress"}</div>
                <div className="text-lg font-black text-emerald-500">
                  {Object.values(completedSteps).filter(Boolean).length} / {compensationProtocol.steps.length}
                </div>
              </div>
            </div>

            {/* Checklist Steps */}
            <div className="space-y-3">
              {compensationProtocol.steps.map((step, idx) => {
                const isDone = !!completedSteps[step.id];
                return (
                  <div
                    key={step.id}
                    onClick={() => toggleStep(step.id)}
                    className={cn(
                      "p-4 rounded-2xl border transition-all cursor-pointer select-none flex items-start gap-3.5 group",
                      isDone 
                        ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-400"
                        : theme === 'light' 
                          ? "bg-white border-zinc-200 hover:border-emerald-500/40 shadow-xs" 
                          : "bg-zinc-800/50 border-zinc-700/60 hover:border-emerald-500/40"
                    )}
                  >
                    <div className={cn(
                      "w-6 h-6 rounded-xl flex items-center justify-center border transition-all shrink-0 mt-0.5",
                      isDone 
                        ? "bg-emerald-500 border-emerald-500 text-white" 
                        : "border-zinc-400 group-hover:border-emerald-500 text-transparent"
                    )}>
                      <Check className="w-3.5 h-3.5" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={cn("text-xs font-black uppercase tracking-wider", isDone ? "line-through opacity-75" : "")}>
                          {isUa ? step.title.ua : step.title.en}
                        </span>
                        <span className="text-[10px] font-bold text-zinc-400 shrink-0 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>{step.timing}</span>
                        </span>
                      </div>
                      <p className={cn("text-xs text-zinc-500 dark:text-zinc-400 mt-1", isDone ? "line-through opacity-70" : "")}>
                        {isUa ? step.description.ua : step.description.en}
                      </p>
                      <div className="mt-2 text-[10px] font-mono text-emerald-500/90 dark:text-emerald-400/90">
                        🔬 {isUa ? step.scientificRationale.ua : step.scientificRationale.en}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Recommended Counterbalance Meal */}
            {compensationProtocol.recommendedCounterMeal && (
              <div className="mt-6 p-4 rounded-2xl bg-zinc-100/70 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-emerald-500">
                    {isUa ? "РЕКОМЕНДОВАНА ВЕЧЕРЯ-КОМПЕНСАТОР" : "RECOMMENDED COUNTERBALANCE DINNER"}
                  </div>
                  <h4 className="text-xs sm:text-sm font-black text-[var(--text)] mt-0.5">
                    {isUa ? compensationProtocol.recommendedCounterMeal.name.ua : compensationProtocol.recommendedCounterMeal.name.en}
                  </h4>
                  <p className="text-[11px] text-zinc-500">
                    {isUa ? compensationProtocol.recommendedCounterMeal.description.ua : compensationProtocol.recommendedCounterMeal.description.en}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5 shrink-0">
                  {compensationProtocol.recommendedCounterMeal.keyIngredients.map((ing, i) => (
                    <span key={i} className="px-2 py-0.5 rounded-lg text-[9px] font-bold bg-zinc-200/60 dark:bg-zinc-700/60 text-zinc-600 dark:text-zinc-300">
                      {ing}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 7. Quick Log Modal */}
      <BiomarkerLogModal
        isOpen={isLogModalOpen}
        onClose={() => setIsLogModalOpen(false)}
        onSaveBiomarker={(newLog) => {
          if (onSaveBiomarker) {
            onSaveBiomarker(newLog);
          }
        }}
        lang={lang}
        theme={theme}
        skin={skin}
      />
    </div>
  );
};
